import React, { useState, useEffect, useRef } from 'react';
import Editor from './Editor';
import { citasAPI, authAPI } from '../services/api';
import { getLabels } from '../utils/userLabels';

function ConsultaForm({ consulta, clientes, onClose, onSubmit }) {
  const [tipoUsuario, setTipoUsuario] = useState('coach');

  useEffect(() => {
    authAPI.check().then(res => {
      if (res.data.authenticated && res.data.user) {
        setTipoUsuario(res.data.user.tipo_usuario || 'coach');
      }
    }).catch(() => {});
  }, []);

  const labels = getLabels(tipoUsuario);
  const [activeTab, setActiveTab] = useState('general');
  const [formData, setFormData] = useState({
    cliente_id: '',
    fecha_consulta: new Date().toISOString().split('T')[0],
    hora_consulta: '',
    numero_sesion: '',
    modalidad: 'Presencial',
    objetivos: '',
    observaciones_cliente: '',
    observaciones_coach: '',
    contexto_sesion_1: '',
    contexto: '',
    estado_cita: 'confirmada',
  });
  const [citaAsociada, setCitaAsociada] = useState(null);
  const [isMaximized, setIsMaximized] = useState(false);
  const lastAutoSaveRef = useRef('');

  const getDraftKey = () => {
    if (consulta && consulta.id) {
      return `consulta_draft_${consulta.id}`;
    }
    return 'consulta_draft_nueva';
  };

  useEffect(() => {
    if (consulta) {
      setFormData({
        cliente_id: consulta.cliente_id.toString(),
        fecha_consulta: consulta.fecha_consulta ? consulta.fecha_consulta.split('T')[0] : new Date().toISOString().split('T')[0],
        hora_consulta: consulta.hora_consulta || '',
        numero_sesion: consulta.numero_sesion || '',
        modalidad: consulta.modalidad || 'Presencial',
        objetivos: consulta.objetivos || '',
        observaciones_cliente: consulta.observaciones_cliente || '',
        observaciones_coach: consulta.observaciones_coach || '',
        contexto_sesion_1: consulta.contexto_sesion_1 || '',
        contexto: consulta.contexto || '',
        estado_cita: 'confirmada', // Valor por defecto, se actualizará si se encuentra la cita
      });
      
      // Buscar la cita asociada
      const buscarCitaAsociada = async () => {
        try {
          const fechaConsulta = consulta.fecha_consulta ? consulta.fecha_consulta.split('T')[0] : null;
          const numeroSesion = consulta.numero_sesion;
          const clienteId = consulta.cliente_id;
          
          
          if (fechaConsulta && numeroSesion !== null && numeroSesion !== undefined && clienteId) {
            // Buscar citas del cliente (sin filtro de fecha primero para ver todas)
            const response = await citasAPI.getAll(clienteId, null, null);
            const citas = response.data || [];
            
            
            // Buscar la cita que coincida con el número de sesión y la fecha
            // Comparar como números para evitar problemas de tipo
            const numeroSesionNum = parseInt(numeroSesion);
            const citaEncontrada = citas.find(cita => {
              if (!cita.fecha_hora) return false;
              
              const citaFecha = new Date(cita.fecha_hora).toISOString().split('T')[0];
              const citaNumeroSesion = cita.numero_sesion ? parseInt(cita.numero_sesion) : null;
              const fechaCoincide = citaFecha === fechaConsulta;
              const numeroCoincide = citaNumeroSesion !== null && citaNumeroSesion === numeroSesionNum;
              
              return fechaCoincide && numeroCoincide;
            });
            
            if (citaEncontrada) {
              setCitaAsociada(citaEncontrada);
              setFormData(prev => ({
                ...prev,
                estado_cita: citaEncontrada.estado || 'confirmada'
              }));
            } else {
              setCitaAsociada(null);
            }
          } else {
            setCitaAsociada(null);
          }
        } catch (err) {
          console.error('Error al buscar cita asociada:', err);
          setCitaAsociada(null);
        }
      };
      
      buscarCitaAsociada();
    }
  }, [consulta]);

  // Cargar borrador local (autoguardado) si existe
  useEffect(() => {
    try {
      const draftKey = getDraftKey();
      const raw = typeof window !== 'undefined' && window.localStorage
        ? window.localStorage.getItem(draftKey)
        : null;
      if (raw) {
        const parsed = JSON.parse(raw);
        setFormData(prev => ({
          ...prev,
          ...parsed,
        }));
        lastAutoSaveRef.current = raw;
      }
    } catch (e) {
      console.error('Error al cargar borrador de notas de sesión:', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [consulta && consulta.id]);

  // Autoguardado cada ~30 segundos (30s desde el último cambio en las notas / formulario)
  useEffect(() => {
    const timeout = setTimeout(() => {
      try {
        const json = JSON.stringify(formData);
        if (json !== lastAutoSaveRef.current) {
          const draftKey = getDraftKey();
          if (typeof window !== 'undefined' && window.localStorage) {
            window.localStorage.setItem(draftKey, json);
          }
          lastAutoSaveRef.current = json;
        }
      } catch (e) {
        console.error('Error en autoguardado de notas de sesión:', e);
      }
    }, 30000); // 30 segundos

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData, consulta && consulta.id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      cliente_id: parseInt(formData.cliente_id),
    };
    
    // Solo incluir estado_cita si estamos editando una sesión existente y hay una cita asociada
    if (!consulta || !citaAsociada) {
      delete data.estado_cita;
    }
    
    onSubmit(data);

    // Limpiar borrador local al guardar explícitamente
    try {
      const draftKey = getDraftKey();
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(draftKey);
      }
      lastAutoSaveRef.current = '';
    } catch (e2) {
      console.error('Error al limpiar borrador de notas de sesión:', e2);
    }
  };

  const toggleMaximize = () => {
    setIsMaximized(!isMaximized);
  };

  return (
    <div
      className="modal"
      style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0, 0, 0, 0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px', overflowY: 'auto', zIndex: 4000 }}
    >
      <div
        className={`modal-content ${isMaximized ? 'maximized' : ''}`}
        style={{ position: 'relative', zIndex: 4001 }}
      >
        <div className="modal-header">
          <h2>{consulta ? labels.editar_sesion : labels.nueva_sesion}</h2>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button 
              className="maximize-btn" 
              onClick={toggleMaximize}
              title={isMaximized ? 'Restaurar' : 'Maximizar'}
            >
              {isMaximized ? '⤓' : '⤢'}
            </button>
            <button className="close-btn" onClick={onClose}>&times;</button>
          </div>
        </div>
        <form onSubmit={handleSubmit}>
          {/* Pestañas */}
          <div className="form-tabs">
            <button
              type="button"
              className={`form-tab ${activeTab === 'general' ? 'active' : ''}`}
              onClick={() => setActiveTab('general')}
            >
              Información General
            </button>
            <button
              type="button"
              className={`form-tab ${activeTab === 'contexto' ? 'active' : ''}`}
              onClick={() => setActiveTab('contexto')}
            >
              Notas de Sesión
            </button>
          </div>

          {/* Contenido de la pestaña General */}
          {activeTab === 'general' && (
            <div className="tab-content">
              <div className="form-group">
                <label>Cliente *</label>
                <select
                  name="cliente_id"
                  value={formData.cliente_id}
                  onChange={handleChange}
                  required
                >
                  <option value="">Selecciona un cliente</option>
                  {clientes.map(cliente => (
                    <option key={cliente.id} value={cliente.id}>
                      {cliente.nombre} {cliente.apellidos}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Fecha de Sesión *</label>
                <input
                  type="date"
                  name="fecha_consulta"
                  value={formData.fecha_consulta}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="form-group">
                <label>Hora de Sesión</label>
                <input
                  type="time"
                  name="hora_consulta"
                  value={formData.hora_consulta}
                  onChange={handleChange}
                />
              </div>
              <div className="form-group">
                <label>{labels.numero_sesion} *</label>
                <input
                  type="number"
                  name="numero_sesion"
                  value={formData.numero_sesion}
                  onChange={handleChange}
                  required
                  min="1"
                />
              </div>
              <div className="form-group">
                <label>Modalidad</label>
                <select
                  name="modalidad"
                  value={formData.modalidad}
                  onChange={handleChange}
                >
                  <option value="Presencial">Presencial</option>
                  <option value="Online">Online</option>
                </select>
              </div>
              {consulta && citaAsociada && (
                <div className="form-group">
                  <label>Estado de la Cita Asociada</label>
                  <select
                    name="estado_cita"
                    value={formData.estado_cita}
                    onChange={handleChange}
                  >
                    <option value="confirmada">Confirmada</option>
                    <option value="cancelada">Cancelada</option>
                    <option value="completada">Completada</option>
                  </select>
                  <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                    Cambiar el estado de la cita asociada a esta sesión
                  </small>
                </div>
              )}
            </div>
          )}

          {/* Contenido de la pestaña Notas de Sesión */}
          {activeTab === 'contexto' && (
            <div className="tab-content">
              <div className="form-group">
                <label>Objetivo de la sesión</label>
                <Editor
                  name="objetivos"
                  value={formData.objetivos}
                  onChange={handleChange}
                  height={isMaximized ? 250 : 150}
                />
              </div>
              {parseInt(formData.numero_sesion) === 1 && (
                <div className="form-group">
                  <label>Contexto {labels.sesion} 1 *</label>
                  <Editor
                    name="contexto_sesion_1"
                    value={formData.contexto_sesion_1}
                    onChange={handleChange}
                    height={isMaximized ? 400 : 300}
                  />
                  <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                    Este campo solo está disponible para la sesión número 1
                  </small>
                </div>
              )}
              <div className="form-group">
                <label>Contexto</label>
                <Editor
                  name="contexto"
                  value={formData.contexto}
                  onChange={handleChange}
                  height={isMaximized ? 400 : 300}
                />
              </div>
              <div className="form-group">
                <label>Observaciones Cliente</label>
                <Editor
                  name="observaciones_cliente"
                  value={formData.observaciones_cliente}
                  onChange={handleChange}
                  height={isMaximized ? 250 : 150}
                />
              </div>
              <div className="form-group">
                <label>Observaciones Coach</label>
                <Editor
                  name="observaciones_coach"
                  value={formData.observaciones_coach}
                  onChange={handleChange}
                  height={isMaximized ? 250 : 150}
                />
              </div>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancelar
            </button>
            <button type="submit" className="btn btn-primary">
              {consulta ? 'Actualizar' : 'Crear'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default ConsultaForm;

